import { ReactNode, useEffect, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

export interface SwipeableTabItem {
  /** Stable value used by the Tabs primitive. */
  value: string;
  /** Trigger label shown in the tab strip. */
  label: ReactNode;
  /** Tab body. */
  content: ReactNode;
  /** Optional element rendered to the left of the label (icon, dot, etc). */
  icon?: ReactNode;
}

interface SwipeableTabsProps {
  items: SwipeableTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  /** Optional class on the outer wrapper. */
  className?: string;
  /** Optional class on the TabsList strip. */
  listClassName?: string;
  /** Padding around each pane. Defaults: mobile `px-3 py-3`, desktop `p-4 md:p-6`. */
  paneClassName?: string;
}

/**
 * SwipeableTabs — desktop renders the standard shadcn Tabs grid; mobile renders a
 * horizontally-scrollable, snap-based pager that the user can swipe left/right,
 * with an auto-scrolling tab strip that keeps the active trigger centered.
 *
 * Extracted from DeviceTabs so every tabbed surface (settings, app detail,
 * configuration detail, etc.) can feel native on phones.
 */
export function SwipeableTabs({
  items,
  value,
  onValueChange,
  className,
  listClassName,
  paneClassName,
}: SwipeableTabsProps) {
  const isMobile = useIsMobile();
  const pagerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const programmaticScroll = useRef(false);

  const activeIndex = Math.max(
    0,
    items.findIndex((i) => i.value === value),
  );

  useEffect(() => {
    if (!isMobile) return;
    const pager = pagerRef.current;
    if (pager) {
      programmaticScroll.current = true;
      pager.scrollTo({ left: activeIndex * pager.clientWidth, behavior: "smooth" });
      window.setTimeout(() => {
        programmaticScroll.current = false;
      }, 350);
    }
    const list = listRef.current;
    if (list) {
      const trigger = list.querySelector<HTMLElement>(`[data-tab-key="${items[activeIndex]?.value}"]`);
      trigger?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeIndex, isMobile, items]);

  const scrollRaf = useRef<number | null>(null);
  const handlePagerScroll = () => {
    if (programmaticScroll.current) return;
    if (scrollRaf.current != null) cancelAnimationFrame(scrollRaf.current);
    scrollRaf.current = requestAnimationFrame(() => {
      const pager = pagerRef.current;
      if (!pager) return;
      const idx = Math.round(pager.scrollLeft / pager.clientWidth);
      const next = items[idx]?.value;
      if (next && next !== value) {
        haptics.trigger("selection");
        onValueChange(next);
      }
    });
  };

  return (
    <div className={cn("w-full", className)}>
      <Tabs value={value} onValueChange={onValueChange} className="w-full">
        <TabsList
          ref={listRef as React.RefObject<HTMLDivElement>}
          className={cn(
            "w-full border-b bg-transparent mx-0 py-0 my-0 px-2 md:px-4",
            isMobile
              ? "flex justify-start gap-1 overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              : "flex flex-wrap justify-start gap-1",
            listClassName,
          )}
        >
          {items.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              data-tab-key={item.value}
              className="snap-start shrink-0 min-h-[44px]"
            >
              {item.icon ? <span className="mr-1.5 inline-flex">{item.icon}</span> : null}
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {isMobile ? (
          <div
            ref={pagerRef}
            onScroll={handlePagerScroll}
            className="flex w-full overflow-x-auto snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {items.map((item) => (
              <div
                key={item.value}
                className={cn(
                  "snap-start shrink-0 w-full px-3 py-3",
                  paneClassName,
                )}
              >
                {item.content}
              </div>
            ))}
          </div>
        ) : (
          <div className={cn("p-4 md:p-6", paneClassName)}>
            {items.map((item) => (
              <TabsContent key={item.value} value={item.value} className="mt-0">
                {item.content}
              </TabsContent>
            ))}
          </div>
        )}
      </Tabs>
    </div>
  );
}

export default SwipeableTabs;
